import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/KpiCard";
import { api, download, errMsg, money } from "@/lib/api";
import { BilanAnnuel, PaiementsTaxes } from "@/components/ConciliationAnnuel";
import { EnvoiConciliation } from "@/components/EnvoiConciliation";

const COLS = [["tps_percue", "TPS perçue"], ["tvq_percue", "TVQ perçue"], ["tps_deductible", "TPS déduct."], ["tvq_deductible", "TVQ déduct."], ["net", "Net à payer"], ["paye_net", "Payé à RQ"]];
const ANNEES = Array.from({ length: 4 }, (_, i) => String(new Date().getFullYear() - i));

const Ecart = ({ v }) => (
  <span className={Math.abs(v) < 0.01 ? "text-emerald-700" : v > 0 ? "text-sky-700" : "text-red-600"}>
    {Math.abs(v) < 0.01 ? "0,00 $ ✓" : `${money(v)} ${v > 0 ? "en trop" : "à payer"}`}
  </span>
);

const Ligne = ({ t, ouvert, basculer }) => (
  <tr className={`border-t border-slate-100 ${t.paiements.length ? "cursor-pointer hover:bg-slate-50" : ""}`} onClick={basculer} data-testid={`conciliation-T${t.trimestre}`}>
    <td className="py-2 pr-2 font-medium whitespace-nowrap">{t.paiements.length ? (ouvert ? <ChevronDown className="inline h-3.5 w-3.5" /> : <ChevronRight className="inline h-3.5 w-3.5" />) : null} T{t.trimestre}</td>
    {COLS.map(([k]) => <td key={k} className="py-2 px-2 text-right tabular whitespace-nowrap" data-testid={`conciliation-T${t.trimestre}-${k}`}>{money(t[k])}</td>)}
    <td className="py-2 pl-2 text-right tabular whitespace-nowrap" data-testid={`conciliation-T${t.trimestre}-ecart`}><Ecart v={t.ecart} /></td>
  </tr>
);

const Paiements = ({ t }) => (
  <tr data-testid={`conciliation-T${t.trimestre}-paiements`}><td colSpan={8} className="bg-slate-50 px-4 py-2">
    {t.paiements.map((p) => <p key={p.id} className="text-xs text-slate-600"><span className="font-mono">{p.date}</span> · {p.description} · <span className="font-mono">{money(p.montant)}</span></p>)}
  </td></tr>
);

// TPS/TVQ perçues − déductibles = net à payer, comparé aux paiements à Revenu Québec, par trimestre
export const ConciliationTaxesPanel = ({ onChange }) => {
  const [annee, setAnnee] = useState(ANNEES[0]);
  const [d, setD] = useState(null);
  const [ouverts, setOuverts] = useState([]);
  const [version, setVersion] = useState(0);
  useEffect(() => { api.get("/conciliation-taxes", { params: { annee } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [annee, version]);
  const recharger = () => { setVersion((v) => v + 1); onChange?.(); };
  const pdf = () => download("/conciliation-taxes/pdf", { annee }, `Conciliation TPS-TVQ ${annee}.pdf`).catch((e) => toast.error(errMsg(e)));
  const action = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Select value={annee} onValueChange={(a) => { setD(null); setAnnee(a); }}><SelectTrigger className="h-8 w-24" data-testid="conciliation-annee"><SelectValue /></SelectTrigger>
        <SelectContent>{ANNEES.map((a) => <SelectItem key={a} value={a} data-testid={`conciliation-annee-${a}`}>{a}</SelectItem>)}</SelectContent></Select>
      <Button size="sm" variant="outline" onClick={pdf} data-testid="conciliation-pdf"><FileText className="h-3.5 w-3.5 mr-1" />PDF</Button>
      <Button size="sm" variant="outline" data-testid="conciliation-excel"
        onClick={() => download("/conciliation-taxes/excel", { annee }, `Conciliation TPS-TVQ ${annee}.xlsx`).catch((e) => toast.error(errMsg(e)))}><FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Excel</Button>
      <EnvoiConciliation annee={annee} />
    </div>
  );
  return (
    <Panel title="Conciliation TPS/TVQ" testId="conciliation-panel" action={action}>
      {!d ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <>
          <p className="mb-3 text-xs text-slate-500">Taxes perçues (dépôts) − taxes déductibles (factures) = net à payer, comparé aux paiements à Revenu Québec de la catégorie « TPS &amp; TVQ ». {d.regle} Cliquez sur un trimestre pour voir ses paiements.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500"><th className="pb-1 text-left">Trim.</th>{COLS.map(([k, l]) => <th key={k} className="pb-1 px-2 text-right whitespace-nowrap">{l}</th>)}<th className="pb-1 pl-2 text-right">Écart</th></tr></thead>
              <tbody>
                {d.trimestres.map((t) => (
                  <Fragment key={t.trimestre}>
                    <Ligne t={t} ouvert={ouverts.includes(t.trimestre)} basculer={() => { if (t.paiements.length) { setOuverts((o) => (o.includes(t.trimestre) ? o.filter((x) => x !== t.trimestre) : [...o, t.trimestre])); } }} />
                    {ouverts.includes(t.trimestre) && <Paiements t={t} />}
                  </Fragment>
                ))}
                <tr className="border-t-2 border-slate-300 font-semibold" data-testid="conciliation-total">
                  <td className="py-2 pr-2">Année</td>
                  {COLS.map(([k]) => <td key={k} className="py-2 px-2 text-right tabular whitespace-nowrap">{money(d.total[k])}</td>)}
                  <td className="py-2 pl-2 text-right tabular whitespace-nowrap"><Ecart v={d.total.ecart} /></td>
                </tr>
              </tbody>
            </table>
          </div>
          <BilanAnnuel d={d} onChange={recharger} />
          <PaiementsTaxes d={d} onChange={recharger} />
        </>
      )}
    </Panel>
  );
};
